import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { flattenCollegeData } from '../../utils/collegeDataFlattener';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  MapPin,
  Globe,
  Building,
  Users,
  DollarSign,
  Calendar,
  Target,
  Trophy,
  CheckCircle,
  Heart,
  Plus,
  ExternalLink,
  Mail,
  Phone,
  Clock,
  BookOpen,
  TrendingUp,
  Award,
  School,
  User,
  Edit2,
  Share2,
  Star,
  Download,
  RefreshCw
} from 'lucide-react';

const CollegeProfile = () => {
  const { collegeId } = useParams();
  const navigate = useNavigate();
  const { currentUser, familyUsers } = useFamilyContext();
  const { addCollege } = useCollegeContext();
  
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentLists, setStudentLists] = useState({});
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('target');
  const [fetchingData, setFetchingData] = useState(false);
  const [dataCompleteness, setDataCompleteness] = useState(0);
  
  // Get children for adding to lists
  const children = familyUsers?.filter(user => user.type !== 'parent') || [];
  
  useEffect(() => {
    loadCollegeData();
    if (currentUser) {
      loadStudentLists();
    }
  }, [collegeId, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadCollegeData = async () => {
    try {
      setLoading(true);
      
      // First try to load from global_colleges table
      let { data, error } = await supabase
        .from('global_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();
      
      // If not found in global_colleges, try college_journey_colleges
      if (error?.code === 'PGRST116' || !data) {
        console.log('College not found in global_colleges, checking college_journey_colleges...');
        const { data: journeyCollegeData, error: journeyError } = await supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('id', collegeId)
          .single();
          
        if (journeyError) {
          console.error('College not found in either table:', journeyError);
          throw new Error('College not found');
        }
        
        // Transform journey college data to match global college format
        data = {
          id: journeyCollegeData.id,
          name: journeyCollegeData.name,
          location: journeyCollegeData.location,
          type: journeyCollegeData.type || 'Private',
          size: journeyCollegeData.size || 'Medium',
          setting: journeyCollegeData.setting || 'Urban',
          website: journeyCollegeData.website || '',
          // Add default values for missing fields
          acceptance_rate: journeyCollegeData.acceptance_rate || null,
          sat_range: journeyCollegeData.sat_range || null,
          act_range: journeyCollegeData.act_range || null,
          tuition: journeyCollegeData.tuition || null,
          // Mark as journey college for different handling
          source: 'college_journey'
        };
      }
        
      setCollege(data);
      // Calculate data completeness
      calculateDataCompleteness(data);
    } catch (error) {
      console.error('Error loading college:', error);
      toast.error('Failed to load college data');
    } finally {
      setLoading(false);
    }
  };

  const calculateDataCompleteness = (collegeData) => {
    const importantFields = [
      'acceptance_rate', 'sat_range', 'act_range', 'tuition_out_of_state', 
      'total_cost', 'ed_deadline', 'ea_deadline', 'rd_deadline', 
      'student_enrollment', 'grad_rate_4yr', 'avg_need_aid'
    ];
    
    const filledFields = importantFields.filter(field => 
      collegeData[field] !== null && 
      collegeData[field] !== undefined && 
      collegeData[field] !== ''
    ).length;
    
    const completeness = Math.round((filledFields / importantFields.length) * 100);
    setDataCompleteness(completeness);
  };

  const fetchLatestData = async () => {
    if (!college?.name) return;
    
    setFetchingData(true);
    try {
      console.log('🔄 Fetching latest data for:', college.name);
      
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'fetch-college-data',
          collegeName: college.name,
          dataFields: 'all'
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // Update the college data in the database
        await updateCollegeData(result.data);
        
        // Reload the college data to show updated information
        await loadCollegeData();
        
        toast.success(`✅ Updated ${college.name} with latest ${result.dataYear} data`);
      } else {
        throw new Error('No data returned from AI service');
      }
    } catch (error) {
      console.error('Error fetching college data:', error);
      toast.error('Failed to fetch latest college data: ' + error.message);
    } finally {
      setFetchingData(false);
    }
  };

  const updateCollegeData = async (newData) => {
    try {
      console.log('📦 Raw AI data received:', newData);
      
      // Flatten the nested AI response data structure
      const flattenedData = flattenCollegeData(newData, { verbose: true });
      
      console.log('📋 Flattened data for database:', flattenedData);
      console.log('📊 Flattened fields count:', Object.keys(flattenedData).length);
      
      // Update in global_colleges if it exists there
      const { error: globalError } = await supabase
        .from('global_colleges')
        .update({
          ...flattenedData,
          data_last_updated: new Date().toISOString()
        })
        .eq('id', collegeId);

      if (globalError) {
        console.error('Error updating global college:', globalError);
        // If global update failed, try journey colleges
        if (college.source === 'college_journey' || globalError.code === '42P01') {
          const { error: journeyError } = await supabase
            .from('college_journey_colleges')
            .update({
              ...flattenedData,
              data_last_updated: new Date().toISOString()
            })
            .eq('id', collegeId);
            
          if (journeyError) {
            console.error('Error updating journey college:', journeyError);
            throw journeyError;
          } else {
            console.log('✅ Successfully updated college_journey_colleges');
          }
        } else {
          throw globalError;
        }
      } else {
        console.log('✅ Successfully updated global_colleges');
      }
    } catch (error) {
      console.error('Error updating college data:', error);
      throw error;
    }
  };
  
  const loadStudentLists = async () => {
    try {
      // First try to find in the legacy colleges table (for global colleges)
      let { data, error } = await supabase
        .from('colleges')
        .select('user_id, category')
        .eq('global_college_id', collegeId);
      
      // If no results, try to find in college_journey_colleges table
      if (!data || data.length === 0) {
        const { data: journeyData, error: journeyError } = await supabase
          .from('college_journey_colleges')
          .select('user_id, category')
          .eq('id', collegeId);
          
        if (journeyError) {
          console.error('Error loading journey college lists:', journeyError);
        } else {
          data = journeyData;
        }
      }
      
      if (error && !data) throw error;
      
      const lists = {};
      (data || []).forEach(item => {
        if (!lists[item.user_id]) {
          lists[item.user_id] = [];
        }
        lists[item.user_id].push(item.category);
      });
      setStudentLists(lists);
    } catch (error) {
      console.error('Error loading student lists:', error);
    }
  };
  
  const addToStudentList = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    
    try {
      const collegeData = {
        name: college.name,
        official_name: college.official_name || college.name,
        location: college.location,
        website: college.website,
        global_college_id: college.id,
        category: selectedCategory,
        status: 'researching'
      };
      
      await addCollege(collegeData, selectedStudent.id);
      toast.success(`Added ${college.name} to ${selectedStudent.displayName || selectedStudent.name}'s ${selectedCategory} list`);
      setShowAddToListModal(false);
      loadStudentLists();
    } catch (error) {
      console.error('Error adding college to list:', error);
      toast.error('Failed to add college to list');
    }
  };
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafaf9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e7e5e4',
            borderTop: '3px solid #0f172a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '16px', color: '#57534e' }}>Loading college profile...</p>
        </div>
      </div>
    );
  }
  
  if (!college) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafaf9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Building size={48} style={{ color: '#a8a29e', marginBottom: '16px' }} />
          <h2 style={{ color: '#0f172a', marginBottom: '8px' }}>College not found</h2>
          <button
            onClick={() => navigate('/college/colleges')}
            style={{
              marginTop: '16px',
              padding: '12px 24px',
              background: '#0f172a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Data Hub
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: '#fafaf9',
        minHeight: '100vh',
        lineHeight: 1.5,
        color: '#0f172a'
      }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '2px solid #e7e5e4',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/college/colleges')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#57534e',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f5f5f4';
                e.target.style.color = '#292524';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#57534e';
              }}
            >
              <ArrowLeft size={20} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#78716c' }}>
              <span style={{ color: '#57534e', fontWeight: 500 }}>College Journey</span>
              <span>›</span>
              <span>Enhanced Data Hub</span>
              <span>›</span>
              <span>{college.name}</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            {/* Data Completeness and Fetch Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: dataCompleteness >= 70 ? '#dcfce7' : dataCompleteness >= 40 ? '#fef3c7' : '#fee2e2',
                color: dataCompleteness >= 70 ? '#166534' : dataCompleteness >= 40 ? '#92400e' : '#991b1b',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500
              }}>
                <Target size={14} />
                <span>{dataCompleteness}% complete</span>
              </div>
              
              <button
                onClick={fetchLatestData}
                disabled={fetchingData}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: fetchingData ? '#f5f5f4' : '#0f172a',
                  color: fetchingData ? '#57534e' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: fetchingData ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: fetchingData ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!fetchingData) {
                    e.target.style.background = '#1e293b';
                  }
                }}
                onMouseOut={(e) => {
                  if (!fetchingData) {
                    e.target.style.background = '#0f172a';
                  }
                }}
              >
                {fetchingData ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Updating...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Fetch Latest Data
                  </>
                )}
              </button>
            </div>

            {college.website && (
              <a
                href={college.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: 'white',
                  color: '#0f172a',
                  border: '2px solid #e7e5e4',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#0f172a';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e7e5e4';
                }}
              >
                <ExternalLink size={16} />
                Visit Website
              </a>
            )}
            {children.length > 0 && (
              <button
                onClick={() => {
                  setSelectedStudent(children[0]);
                  setShowAddToListModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#6d28d9';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#7c3aed';
                }}
              >
                <Plus size={16} />
                Add to List
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        {/* College Header */}
        <div style={{
          background: 'white',
          border: '2px solid #e7e5e4',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#0f172a',
            marginBottom: '12px'
          }}>
            {college.name}
          </h1>
          {college.official_name && college.official_name !== college.name && (
            <p style={{
              fontSize: '16px',
              color: '#57534e',
              marginBottom: '16px'
            }}>
              {college.official_name}
            </p>
          )}
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            alignItems: 'center'
          }}>
            {college.location && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#57534e'
              }}>
                <MapPin size={18} />
                <span>{college.location}</span>
              </div>
            )}
            {college.type && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Building size={18} style={{ color: '#57534e' }} />
                <span style={{
                  padding: '4px 12px',
                  background: college.type === 'public' ? '#dcfce7' : '#fef3c7',
                  color: college.type === 'public' ? '#166534' : '#92400e',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'capitalize'
                }}>
                  {college.type.replace('_', ' ')}
                </span>
              </div>
            )}
          </div>
          
          {/* Student Lists */}
          {Object.keys(studentLists).length > 0 && (
            <div style={{
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '2px solid #e7e5e4'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#0f172a',
                marginBottom: '12px'
              }}>
                On Student Lists
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {Object.entries(studentLists).map(([userId, categories]) => {
                  const student = familyUsers?.find(u => u.id === userId);
                  if (!student) return null;
                  
                  return categories.map(category => (
                    <div
                      key={`${userId}-${category}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        background: '#f5f5f4',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <User size={16} style={{ color: '#57534e' }} />
                      <span style={{ color: '#0f172a', fontWeight: 500 }}>
                        {student.displayName || student.name}
                      </span>
                      <span style={{ color: '#57534e' }}>•</span>
                      <span style={{
                        color: category === 'reach' ? '#fbbf24' : 
                               category === 'target' ? '#0d9488' : 
                               category === 'safety' ? '#3b82f6' : '#57534e',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {category}
                      </span>
                    </div>
                  ));
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* College Details Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {/* Basic Information */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <School size={20} />
              Basic Information
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(college.enrollment || college.student_enrollment) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Enrollment</span>
                  <span style={{ fontWeight: 500 }}>{(college.enrollment || college.student_enrollment).toLocaleString()}</span>
                </div>
              )}
              {college.usnwr_ranking && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>National Ranking</span>
                  <span style={{ fontWeight: 500 }}>#{college.usnwr_ranking}</span>
                </div>
              )}
              {college.acceptance_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Acceptance Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.acceptance_rate * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.ed_acceptance_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Early Decision Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.ed_acceptance_rate * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.yield_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Yield Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.yield_rate * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.retention_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Retention Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.retention_rate * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.graduation_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Graduation Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.graduation_rate * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.size_category && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Size Category</span>
                  <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{college.size_category}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Financial Information */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <DollarSign size={20} />
              Financial Information
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.tuition_in_state && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>In-State Tuition</span>
                  <span style={{ fontWeight: 500 }}>${college.tuition_in_state.toLocaleString()}</span>
                </div>
              )}
              {(college.tuition_out_state || college.tuition_out_of_state) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Out-of-State Tuition</span>
                  <span style={{ fontWeight: 500 }}>${(college.tuition_out_state || college.tuition_out_of_state).toLocaleString()}</span>
                </div>
              )}
              {(college.room_and_board || college.room_board) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Room & Board</span>
                  <span style={{ fontWeight: 500 }}>${(college.room_and_board || college.room_board).toLocaleString()}</span>
                </div>
              )}
              {college.total_cost && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Total Cost of Attendance</span>
                  <span style={{ fontWeight: 500 }}>${college.total_cost.toLocaleString()}</span>
                </div>
              )}
              {(college.average_aid || college.avg_need_aid) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Need-Based Aid</span>
                  <span style={{ fontWeight: 500 }}>${(college.average_aid || college.avg_need_aid).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Test Scores */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Award size={20} />
              Test Scores
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(college.sat_range_low && college.sat_range_high) || college.sat_range && (
                <div>
                  <span style={{ color: '#57534e' }}>SAT Range (25th-75th percentile)</span>
                  <div style={{ fontWeight: 500, marginTop: '4px' }}>
                    {college.sat_range || `${college.sat_range_low} - ${college.sat_range_high}`}
                  </div>
                </div>
              )}
              {college.sat_math_range && (
                <div>
                  <span style={{ color: '#57534e' }}>SAT Math Range</span>
                  <div style={{ fontWeight: 500, marginTop: '4px' }}>
                    {college.sat_math_range}
                  </div>
                </div>
              )}
              {college.sat_reading_range && (
                <div>
                  <span style={{ color: '#57534e' }}>SAT Reading Range</span>
                  <div style={{ fontWeight: 500, marginTop: '4px' }}>
                    {college.sat_reading_range}
                  </div>
                </div>
              )}
              {(college.act_range_low && college.act_range_high) || college.act_range && (
                <div>
                  <span style={{ color: '#57534e' }}>ACT Range (25th-75th percentile)</span>
                  <div style={{ fontWeight: 500, marginTop: '4px' }}>
                    {college.act_range || `${college.act_range_low} - ${college.act_range_high}`}
                  </div>
                </div>
              )}
              {college.test_optional !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Test Optional</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.test_optional ? '#dcfce7' : '#fee2e2',
                    color: college.test_optional ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.test_optional ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {college.test_blind !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Test Blind</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.test_blind ? '#dcfce7' : '#fee2e2',
                    color: college.test_blind ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.test_blind ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {college.super_scoring !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Super Scoring</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.super_scoring ? '#dcfce7' : '#fee2e2',
                    color: college.super_scoring ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.super_scoring ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Academic Programs & Requirements */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <BookOpen size={20} />
              Academic Programs & Requirements
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.num_essays && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Number of Essays</span>
                  <span style={{ fontWeight: 500 }}>{college.num_essays}</span>
                </div>
              )}
              {college.teacher_recs && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Teacher Recommendations</span>
                  <span style={{ fontWeight: 500 }}>{college.teacher_recs}</span>
                </div>
              )}
              {college.interview_policy && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Interview Policy</span>
                  <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{college.interview_policy.replace('_', ' ')}</span>
                </div>
              )}
              {college.avg_gpa_weighted && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Weighted GPA</span>
                  <span style={{ fontWeight: 500 }}>{college.avg_gpa_weighted}</span>
                </div>
              )}
              {college.avg_gpa_unweighted && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Unweighted GPA</span>
                  <span style={{ fontWeight: 500 }}>{college.avg_gpa_unweighted}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Application Deadlines */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Calendar size={20} />
              Application Deadlines
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.ed_deadline && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Early Decision</span>
                  <span style={{ fontWeight: 500 }}>{new Date(college.ed_deadline).toLocaleDateString()}</span>
                </div>
              )}
              {college.ed2_deadline && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Early Decision II</span>
                  <span style={{ fontWeight: 500 }}>{new Date(college.ed2_deadline).toLocaleDateString()}</span>
                </div>
              )}
              {college.ea_deadline && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Early Action</span>
                  <span style={{ fontWeight: 500 }}>{new Date(college.ea_deadline).toLocaleDateString()}</span>
                </div>
              )}
              {college.rd_deadline && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Regular Decision</span>
                  <span style={{ fontWeight: 500 }}>{new Date(college.rd_deadline).toLocaleDateString()}</span>
                </div>
              )}
              {college.finaid_deadline && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Financial Aid</span>
                  <span style={{ fontWeight: 500 }}>{new Date(college.finaid_deadline).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Campus Life & Demographics */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Users size={20} />
              Campus Life & Demographics
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.student_faculty_ratio && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Student-Faculty Ratio</span>
                  <span style={{ fontWeight: 500 }}>{college.student_faculty_ratio}</span>
                </div>
              )}
              {college.percent_out_of_state && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Out-of-State Students</span>
                  <span style={{ fontWeight: 500 }}>{(college.percent_out_of_state * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.percent_international && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>International Students</span>
                  <span style={{ fontWeight: 500 }}>{(college.percent_international * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.avg_class_size && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Class Size</span>
                  <span style={{ fontWeight: 500 }}>{college.avg_class_size}</span>
                </div>
              )}
              {college.campus_setting && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Campus Setting</span>
                  <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{college.campus_setting}</span>
                </div>
              )}
              {college.religious_affiliation && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Religious Affiliation</span>
                  <span style={{ fontWeight: 500 }}>{college.religious_affiliation}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Outcomes & Career Data */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <TrendingUp size={20} />
              Outcomes & Career Data
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.grad_rate_4yr && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>4-Year Graduation Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.grad_rate_4yr * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.grad_rate_6yr && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>6-Year Graduation Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.grad_rate_6yr * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.avg_salary && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Starting Salary</span>
                  <span style={{ fontWeight: 500 }}>${college.avg_salary.toLocaleString()}</span>
                </div>
              )}
              {college.employment_rate && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Employment Rate</span>
                  <span style={{ fontWeight: 500 }}>{(college.employment_rate * 100).toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Financial Aid Details */}
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Heart size={20} />
              Financial Aid Details
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {college.avg_merit_aid && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Average Merit Aid</span>
                  <span style={{ fontWeight: 500 }}>${college.avg_merit_aid.toLocaleString()}</span>
                </div>
              )}
              {college.percent_receiving_aid && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Students Receiving Aid</span>
                  <span style={{ fontWeight: 500 }}>{(college.percent_receiving_aid * 100).toFixed(1)}%</span>
                </div>
              )}
              {college.need_blind !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Need-Blind Admissions</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.need_blind ? '#dcfce7' : '#fee2e2',
                    color: college.need_blind ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.need_blind ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {college.meets_full_need !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>Meets Full Need</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.meets_full_need ? '#dcfce7' : '#fee2e2',
                    color: college.meets_full_need ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.meets_full_need ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {college.no_loan_policy !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#57534e' }}>No-Loan Policy</span>
                  <span style={{
                    padding: '4px 12px',
                    background: college.no_loan_policy ? '#dcfce7' : '#fee2e2',
                    color: college.no_loan_policy ? '#166534' : '#991b1b',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {college.no_loan_policy ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Programs & Opportunities */}
        {(Array.isArray(college.special_programs) && college.special_programs.length > 0) || 
         (Array.isArray(college.institutional_priorities) && college.institutional_priorities.length > 0) ? (
          <div style={{
            background: 'white',
            border: '2px solid #e7e5e4',
            borderRadius: '12px',
            padding: '24px',
            marginTop: '20px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Star size={20} />
              Special Programs & Priorities
            </h2>
            
            {Array.isArray(college.special_programs) && college.special_programs.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: '8px'
                }}>
                  Special Programs
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {college.special_programs.map((program, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: '#f0f9ff',
                        color: '#0369a1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {program}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {Array.isArray(college.institutional_priorities) && college.institutional_priorities.length > 0 && (
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: '8px'
                }}>
                  Institutional Priorities
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {college.institutional_priorities.map((priority, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: '#fef3c7',
                        color: '#92400e',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {priority}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      
      {/* Add to List Modal */}
      {showAddToListModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#0f172a'
              }}>
                Add {college.name} to List
              </h2>
              <button
                onClick={() => setShowAddToListModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#57534e',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px'
                }}
              >
                <ExternalLink size={20} style={{ transform: 'rotate(45deg)' }} />
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: '6px'
                }}>
                  Select Student
                </label>
                <select
                  value={selectedStudent?.id || ''}
                  onChange={(e) => {
                    const student = children.find(c => c.id === e.target.value);
                    setSelectedStudent(student || null);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e7e5e4',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="">Choose a student...</option>
                  {children.map(child => (
                    <option key={child.id} value={child.id}>
                      {child.displayName || child.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: '6px'
                }}>
                  Select List Category
                </label>
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  {[
                    { value: 'reach', label: 'reach', icon: Trophy, color: '#fbbf24' },
                    { value: 'target', label: 'Target', icon: Target, color: '#0d9488' },
                    { value: 'safety', label: 'Safety', icon: CheckCircle, color: '#3b82f6' }
                  ].map(category => (
                    <button
                      key={category.value}
                      onClick={() => setSelectedCategory(category.value)}
                      style={{
                        flex: 1,
                        padding: '16px',
                        border: '2px solid',
                        borderColor: selectedCategory === category.value ? category.color : '#e7e5e4',
                        borderRadius: '8px',
                        background: selectedCategory === category.value ? category.color + '10' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <category.icon size={24} style={{ color: category.color }} />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: selectedCategory === category.value ? category.color : '#57534e'
                      }}>
                        {category.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => setShowAddToListModal(false)}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  color: '#57534e',
                  border: '2px solid #e7e5e4',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={addToStudentList}
                disabled={!selectedStudent}
                style={{
                  padding: '12px 20px',
                  background: !selectedStudent ? '#94a3b8' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: !selectedStudent ? 'not-allowed' : 'pointer'
                }}
              >
                Add to List
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </>
  );
};

export default CollegeProfile;